/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { getCheckoutState, IGiftCardExtend } from '@msdyn365-commerce/global-state';
import {
    getDeliveryOptionsAsync,
    retrieveCardPaymentAcceptResultAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    Address,
    CardPaymentAcceptResult,
    Cart,
    FilterDeliveryModeOption
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classNames from 'classnames';
import * as React from 'react';

import { CardPaymentAcceptPoint } from '@msdyn365-commerce/retail-proxy';
import { EnabledPaymentsForOBO } from '@msdyn365-commerce-modules/checkout-utilities';
import { computed } from 'mobx';
import { ObjectExtensions, QueryResultSettingsProxy, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ApplePayButtonState, ICustomCheckoutApplePayData } from './custom-checkout-apple-pay.data';
import { ICustomCheckoutApplePayProps } from './custom-checkout-apple-pay.props.autogenerated';
import { getThreeLetterCountryCode, ErrorComponent, PlaceOrder } from '@msdyn365-commerce-modules/checkout';
import { triggerDLEvent } from '../../themes/venchi-v2-1/utilities/datalayer';
import { GtmEvents } from '../../themes/venchi-v2-1/utilities/datalayer/types';
import { setCheckoutCartInLocalStorage, setOrderPlacedStatusLocalStorage } from '../../themes/venchi-v2-1/utilities/datalayer/helpers';

enum ApplePayPaymentAuthorizationResult {
    STATUS_SUCCESS = 0,
    STATUS_FAILURE = 1
}

interface IApplePayLine {
    label: string;
    type: string;
    amount: string;
}

interface IValidationEvent {
    validationUrl: string;
}

interface IResult {
    newTotal: IApplePayLine;
}

interface IAuthorizeEvent {
    paymentToken: string;
    shippingContact?: IShippingContactSelectedEvent;
}

interface IAuthorizeResult {
    resultCode: ApplePayPaymentAuthorizationResult;
}

interface IPaymentMethodEvent {
    type?: string;
}

/**
 * An interface that describes the shipping method for delivering physical goods.
 * Link to Apple doc - https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingmethod
 */
interface IApplePayShippingMethod {
    /**
     * A short description of the shipping method.
     */
    label?: string;

    /**
     * The nonnegative cost associated with this shipping method.
     */
    amount?: string;

    /**
     * Additional description of the shipping method.
     */
    detail?: string;

    /**
     * A client-defined value used to identify this shipping method.
     */
    identifier?: string;
}

/**
 * Contact information fields to use for billing and shipping contact information.
 * Link to Apple doc - https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentcontact
 */
interface IShippingContactSelectedEvent {
    /**
     * A phone number for the contact.
     */
    phoneNumber?: string;

    /**
     * An email address for the contact.
     */
    emailAddress?: string;

    /**
     * The contact’s given name.
     */
    givenName?: string;

    /**
     * The contact’s family name.
     */
    familyName?: string;

    /**
     * The street portion of the address for the contact.
     */
    addressLines?: string[];

    /**
     * Additional information associated with the location, typically defined at the city or town level (such as district or neighborhood), in a postal address.
     */
    subLocality?: string;

    /**
     * The city for the contact.
     */
    locality?: string;

    /**
     * The zip code or postal code, where applicable, for the contact.
     */
    postalCode?: string;

    /**
     * The subAdministrative area (such as a county or other region) in a postal address.
     */
    subAdministrativeArea?: string;

    /**
     * The state for the contact.
     */
    administrativeArea?: string;

    /**
     * The name of the country or region for the contact.
     */
    country?: string;

    /**
     * The contact’s two-letter ISO 3166 country code.
     */
    countryCode?: string;
}

interface IShippingMethodSelectedEvent {
    /**
     * The identifier of selected shipping method.
     */
    id: string;
}

interface IAppleSessionHandler {
    onAuthorize(authorizeEvent: IAuthorizeEvent): Promise<IAuthorizeResult>;
    onCancel(): void;
    onPaymentMethodUpdate(paymentMethodEvent: IPaymentMethodEvent): Promise<IResult>;
    onValidation(validationEvent: IValidationEvent): Promise<string>;

    onShippingContactSelected?(shippingContactSelectedEvent: IShippingContactSelectedEvent): Promise<IResult>;
    onShippingMethodSelected?(shippingMethodSelectedEvent: IShippingMethodSelectedEvent): Promise<IResult>;
}

export interface ICheckoutApplePayViewProps extends ICustomCheckoutApplePayProps<{}> {
    className?: string;
    checkoutApplePay: IModuleProps;
    applePayButton?: React.ReactNode;
    alert?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

export interface ICheckoutApplePayModuleProps extends ICustomCheckoutApplePayProps<ICustomCheckoutApplePayData> {}

interface ICheckoutApplePayState {
    applePayButtonState: ApplePayButtonState;
    paymentConnectorId?: string;
    lineDisplayName?: string;
    errorMessage?: string;
    shippingContact?: Address;
    shippingMethodId?: string;
}
/**
 *
 * CheckoutApplePay component
 * @extends {React.Component<ICheckoutApplePayModuleProps, ICheckoutApplePayState>}
 */
export class CheckoutApplePay extends React.Component<ICheckoutApplePayModuleProps, ICheckoutApplePayState> {
    private readonly moduleClassName: string = 'ms-checkout-apple-pay';
    private readonly applePaySdkId = 'd365-apple-pay-sdk';
    private readonly zeroAmount: number = 0;

    @computed private get getLoyaltyAmount(): number {
        const {
            data: { checkout }
        } = this.props;
        const checkoutState = checkout?.result;
        return checkoutState?.loyaltyAmount ? checkoutState.loyaltyAmount : this.zeroAmount;
    }

    @computed private get getGiftCardTotalAmount(): number {
        const {
            data: { checkout }
        } = this.props;
        const checkoutState = checkout?.result;
        if (!checkoutState || !checkoutState.giftCardExtends) {
            return this.zeroAmount;
        }
        return checkoutState.giftCardExtends.reduce((count: number, giftCard: IGiftCardExtend) => {
            return count + (giftCard.Balance || this.zeroAmount);
        }, 0);
    }

    @computed private get getCustomerAccountAmount(): number {
        const {
            data: { checkout }
        } = this.props;
        const checkoutState = checkout?.result;

        return checkoutState?.customerAccountAmount ?? this.zeroAmount;
    }

    @computed private get amountDue(): number {
        const {
            data: { checkout }
        } = this.props;

        const cart = checkout?.result?.checkoutCart.cart;
        if (!cart || !cart.AmountDue) {
            return this.zeroAmount;
        }

        return Math.max(
            this.zeroAmount,
            cart.AmountDue - this.getGiftCardTotalAmount - this.getLoyaltyAmount - this.getCustomerAccountAmount
        );
    }

    @computed private get merchantName(): string {
        return this.state.lineDisplayName || '';
    }

    public constructor(props: ICustomCheckoutApplePayProps<ICustomCheckoutApplePayData>) {
        super(props);
        this.state = {
            applePayButtonState: ApplePayButtonState.Unknown
        };
    }

    public async componentDidMount() {
        const {
            data: { cardPaymentAcceptPoint },
            telemetry
        } = this.props;

        // load JS portion of payment connector for ApplePay
        await cardPaymentAcceptPoint;

        if (cardPaymentAcceptPoint?.result !== undefined) {
            this.loadPaymentConnectorSdk(cardPaymentAcceptPoint.result);

            // load official ApplePay JS SDK.
            const script = document.createElement('script');
            script.onload = () => {
                let isApplePayAvailable = false;
                if (typeof window.isApplePayAvailable === 'function') {
                    isApplePayAvailable = window.isApplePayAvailable();
                }

                telemetry.information(`is Apple Pay available: ${isApplePayAvailable}`);
                return this.setState({
                    applePayButtonState: isApplePayAvailable ? ApplePayButtonState.Available : ApplePayButtonState.NotAvailable,
                    paymentConnectorId: cardPaymentAcceptPoint.result?.PaymentConnectorId
                });
            };

            script.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
            script.async = false;
            script.onerror = () => telemetry.error('unable to load Apple Pay SDK');
            document.body.appendChild(script);
        } else {
            telemetry.error('unable to get Apple Pay connector SDK');
        }
    }

    public render(): JSX.Element | null {
        const {
            config: { className },
            resources,
            telemetry
        } = this.props;

        if (this.props.context.request.user.isOBORequest && EnabledPaymentsForOBO.ExpressPayment === 0) {
            telemetry.information('isOBORequest is enabled, apple pay module wont render');
            return null;
        }

        if (this.state.applePayButtonState === ApplePayButtonState.NotAvailable) {
            return null;
        }

        let applePayButton: React.ReactNode;
        switch (this.state.applePayButtonState) {
            case ApplePayButtonState.Available:
                applePayButton = (
                    <div className='apple-pay-button apple-pay-button-black' id='apple-pay' onClick={this.handlePayButton}></div>
                );
                break;

            case ApplePayButtonState.Unknown:
            default:
                applePayButton = <div />;
                break;
        }

        const viewProps: ICheckoutApplePayViewProps = {
            ...this.props,
            ...this.state,
            className,
            checkoutApplePay: {
                moduleProps: this.props,
                className: classNames(this.moduleClassName, className)
            },
            alert: this.state.errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: this.state.errorMessage }} />
            ),
            applePayButton
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Handler for Apple Pay button.
     */
    public handlePayButton = async () => {
        const { config, resources, telemetry } = this.props;

        const handler: IAppleSessionHandler = {
            onAuthorize: this.authorizePayment,
            onCancel: this.cancelPayment,
            onPaymentMethodUpdate: this.updatePaymentMethod,
            onValidation: this.validateMerchant
        };

        // Set shipping related callbacks if corresponding options enabled.
        if (config.shouldRequestShippingAddress) {
            handler.onShippingContactSelected = this.shippingContactSelected;
            handler.onShippingMethodSelected = this.shippingMethodSelected;
        }

        try {
            // calling connector JS to create payment session.
            const displayName = window.CreateApplePaySession(handler, this.amountDue.toString());
            this.setState({
                lineDisplayName: displayName
            });
        } catch (error) {
            this.showErrorMessage(resources.genericErrorMessage);
            telemetry.error(`unable to create Apple Pay session: ${error}`);
        }
    };

    /**
     * Call CSU checkout API with Apple Pay token.
     * @param paymentToken {string} The gateway payment token
     */
    private checkout = async (paymentToken: string) => {
        const {
            data: { checkout },
            context: { actionContext }
        } = this.props;
        const { paymentConnectorId } = this.state;

        const checkoutState = await getCheckoutState(actionContext);
        const cartState = checkoutState.checkoutCart;

        const channelConfiguration = actionContext.requestContext.channel;
        if (!channelConfiguration) {
            throw new Error('unable to get channel configuration');
        }

        if (checkout !== null) {
            const cardPaymentAcceptResult: CardPaymentAcceptResult = {
                AccessCode: paymentToken,
                Settings: {
                    PaymentConnectorId: paymentConnectorId
                }
            };

            await checkout?.result?.updateCardPaymentAcceptResult({ newCardPaymentAcceptResult: cardPaymentAcceptResult });
            await PlaceOrder(
                actionContext,
                checkoutState,
                undefined, // orderedProducts
                true, // redirect,
                cartState.cart.Version,
                false // isPaymentVerificationRedirection
            );
        }
    };

    /**
     * Load JS SDK from payment connector.
     * @param cardPaymentAcceptPoint {CardPaymentAcceptPoint} The card payment accept point object from CSU.
     */
    private loadPaymentConnectorSdk = (cardPaymentAcceptPoint: CardPaymentAcceptPoint) => {
        if (!cardPaymentAcceptPoint.AcceptPageContent) {
            this.props.telemetry.error(`Card payment accept page content can't be null or empty`);
            return;
        }

        const connectorSdk: string = cardPaymentAcceptPoint.AcceptPageContent || '';
        const sdk = document.createElement('script');
        sdk.id = this.applePaySdkId;
        sdk.type = 'text/javascript';
        sdk.appendChild(document.createTextNode(connectorSdk));
        document.body.appendChild(sdk);
    };

    /**
     * Validate Apple Pay merchant account with gateway.
     * @param validationEvent {IValidationEvent} The object containing merchant validation data.
     * @returns The response from CSU with validated data.
     */
    private validateMerchant = async (validationEvent: IValidationEvent): Promise<string> => {
        const {
            data: { checkout, cardPaymentAcceptPoint },
            resources,
            telemetry
        } = this.props;

        telemetry.information('starting merchant validation for Apple Pay');
        const validationUrl = validationEvent?.validationUrl || '';
        if (!validationUrl) {
            telemetry.error(`Apple Pay validation failed: url can't be null or empty`);
            return Promise.reject('Validation url can not be null or empty');
        }

        const cartId = checkout?.result?.checkoutCart.cart.Id || '';
        if (!cartId) {
            telemetry.error(`Apple Pay validation failed: cart can't be null or empty`);
            return Promise.reject('Cart identifier can not be null or empty');
        }

        const paymentConnectorId = cardPaymentAcceptPoint?.result?.PaymentConnectorId || '';
        if (!paymentConnectorId) {
            telemetry.error(`Apple Pay validation failed: payment connector identifier can't be null or empty`);
            return Promise.reject('Connector identifier can not be null or empty');
        }

        const validationObject = {
            domainName: window.location.hostname,
            validationUrl: validationUrl
        };
        const validationBody = JSON.stringify(validationObject);
        const validationData = await retrieveCardPaymentAcceptResultAsync(
            { callerContext: this.props.context.actionContext },
            btoa(validationBody),
            [],
            cartId,
            { PaymentConnectorId: paymentConnectorId }
        ).catch(error => {
            this.showErrorMessage(resources.genericErrorMessage);
            telemetry.error(`Apple Pay validation failed: ${error}`);
            return Promise.reject(error);
        });

        telemetry.information('merchant validation for Apple Pay completed');
        return Promise.resolve(JSON.stringify(validationData));
    };

    /**
     * Authorize Apple Pay token by calling checkout API.
     * @param authorizeEvent {IAuthorizeEvent} The object containing authorization information.
     * @returns The authorization result.
     */
    private authorizePayment = async (authorizeEvent: IAuthorizeEvent): Promise<IAuthorizeResult> => {
        const { resources, telemetry } = this.props;

        telemetry.information('starting authorization for Apple Pay');
        try {
            await this.updateContactInfo(authorizeEvent.shippingContact);
            await this.checkout(btoa(JSON.stringify(authorizeEvent)));

            setOrderPlacedStatusLocalStorage(true);
            setCheckoutCartInLocalStorage(this.props.data.checkout?.result?.checkoutCart.cart);

            telemetry.information('Apple Pay authorization completed');
            triggerDLEvent({
                event: GtmEvents.CHECKOUT_APPLE_PAY_SUCCESS
            });
            return Promise.resolve({ resultCode: ApplePayPaymentAuthorizationResult.STATUS_SUCCESS });
        } catch (error) {
            this.showErrorMessage(resources.genericErrorMessage);
            telemetry.error(`Apple Pay authorization failed: ${error as string}`);
            triggerDLEvent({
                event: GtmEvents.CHECKOUT_APPLE_PAY_ERROR
            });
            return Promise.reject(error as string);
        }
    };

    /**
     * Callback to notify Apple Pay sheet was closed/canceled.
     */
    private cancelPayment = (): void => {
        this.props.telemetry.information('Apple Pay popup canceled');
        triggerDLEvent({
            event: GtmEvents.CHECKOUT_APPLE_PAY_CANCEL
        });
    };

    /**
     * The callback for Apple Pay payment method confirmation.
     * @param paymentMethodEvent {IPaymentMethodEvent} The payment method event object.
     * @returns The transaction result object.
     */
    private updatePaymentMethod = async (paymentMethodEvent: IPaymentMethodEvent): Promise<IResult> => {
        return Promise.resolve({
            newTotal: {
                label: this.merchantName,
                type: 'final',
                amount: `${this.amountDue}`
            }
        });
    };

    /**
     * The callback for Apple Pay shipping contact confirmation.
     * @param shippingContact {IShippingContactSelectedEvent} The shipping contact event object.
     * @returns The transaction result object.
     */
    private shippingContactSelected = async (shippingContact: IShippingContactSelectedEvent): Promise<IResult> => {
        const { resources, telemetry } = this.props;

        // construct Address object from Apple Pay shipping contact.
        const shippingAddress: Address = this.getAddressFromShippingContact(shippingContact);
        const shippingMethods = await this.getShippingMethods(shippingAddress);
        if (ObjectExtensions.isNullOrUndefined(shippingMethods) || shippingMethods.length === 0) {
            return Promise.reject();
        }

        try {
            const shippingMethodId = shippingMethods.at(0)?.identifier || '';
            await this.updateShippingInfo(shippingMethodId, shippingAddress);

            // save shipping contact to update shipping method later.
            this.setState({
                shippingContact: shippingAddress,
                shippingMethodId: shippingMethodId
            });
        } catch (e) {
            telemetry.error(`unable to update shipping info after shipping method change: ${e}`);
            this.showErrorMessage(resources.genericErrorMessage);
        }

        return Promise.resolve({
            newShippingMethods: shippingMethods,
            newTotal: {
                label: this.merchantName,
                type: 'final',
                amount: `${this.amountDue}`
            }
        });
    };

    /**
     * The callback for Apple Pay shipping method confirmation.
     * @param shippingMethodSelectedEvent {IShippingMethodSelectedEvent} The shipping method event object.
     * @returns The transaction result object.
     */
    private shippingMethodSelected = async (shippingMethod: IShippingMethodSelectedEvent): Promise<IResult> => {
        const { resources, telemetry } = this.props;

        try {
            await this.updateShippingInfo(shippingMethod.id, this.state.shippingContact);

            // save selected shipping method.
            this.setState({ shippingMethodId: shippingMethod.id });
        } catch (e) {
            telemetry.error(`unable to update address after shipping method change: ${e}`);
            this.showErrorMessage(resources.genericErrorMessage);
        }

        return Promise.resolve({
            newTotal: {
                label: this.merchantName,
                type: 'final',
                amount: `${this.amountDue}`
            }
        });
    };

    /**
     * Helper function to obtain shipping methods available for Apple contact address.
     * @param address {Address} The shipping address.
     * @returns The cart delivery options.
     */
    private getShippingMethods = async (address: Address): Promise<IApplePayShippingMethod[]> => {
        const {
            context: { actionContext },
            data: { checkout },
            telemetry
        } = this.props;

        const cartId = (checkout?.result?.checkoutCart?.cart as Cart).Id;
        const deliveryOptionFilter: FilterDeliveryModeOption = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
        const appleDeliveryOptions: IApplePayShippingMethod[] = [];
        try {
            const deliveryOptions = await getDeliveryOptionsAsync(
                {
                    callerContext: actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(actionContext)
                },
                cartId,
                address,
                [],
                deliveryOptionFilter,
                false
            );
            deliveryOptions.forEach(element =>
                appleDeliveryOptions.push({
                    label: element.Description,
                    amount: `${element.ShippingChargeAmount}`,
                    detail: element.Description,
                    identifier: element.Code
                })
            );
        } catch (e) {
            telemetry.error('unable to get delivery options for Apple Pay contact');
        }

        return appleDeliveryOptions;
    };

    /**
     *  Helper function to convert Apple Pay shipping contact into Address object.
     * @param shippingContact {IShippingContactSelectedEvent} The shipping contact provided by Apple Pay SDK.
     * @returns The Address object.
     */
    private getAddressFromShippingContact = (shippingContact: IShippingContactSelectedEvent) => {
        const shippingAddress: Address = {
            ThreeLetterISORegionName: getThreeLetterCountryCode(shippingContact?.countryCode || ''),
            Name: this.getContactName(shippingContact),
            Street: shippingContact?.addressLines?.at(0),
            StreetNumber: shippingContact?.addressLines?.at(1),
            City: shippingContact?.locality,
            State: shippingContact?.administrativeArea,
            ZipCode: shippingContact?.postalCode
        };

        return shippingAddress;
    };

    /**
     * Helper function to return formatted contact name.
     * @param shippingContact {IShippingContactSelectedEvent} The shipping contact provided by Apple Pay SDK.
     * @returns The formatted contact name, i.e. 'Test User', 'Test', 'User' or empty string if both given and family names are missing.
     */
    private getContactName = (shippingContact: IShippingContactSelectedEvent) => {
        const givenName = shippingContact?.givenName || '';
        const familyName = shippingContact?.familyName || '';
        return `${givenName}${
            StringExtensions.isNullOrWhitespace(givenName) || StringExtensions.isNullOrWhitespace(familyName) ? '' : ' '
        }${familyName}`;
    };

    private updateContactInfo = async (shippingContact?: IShippingContactSelectedEvent) => {
        const {
            data: { checkout },
            telemetry
        } = this.props;

        if (shippingContact) {
            const shippingAddress: Address = this.getAddressFromShippingContact(shippingContact);
            const shippingMethodId = this.state.shippingMethodId || '';
            await this.updateShippingInfo(shippingMethodId, shippingAddress);
            const newGuestCheckoutEmail = shippingContact.emailAddress || '';
            if (!StringExtensions.isNullOrWhitespace(newGuestCheckoutEmail)) {
                await checkout?.result?.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
            } else {
                telemetry.warning('email address missing in shipping contact from Apple');
            }
        } else {
            telemetry.information('no shipping information provided');
        }
    };

    /**
     * Update shipping information on cart.
     * @param shippingMethodId {string} The identifier for shipping method.
     * @param shippingAddress  {Address} The shipping address.
     */
    private updateShippingInfo = async (shippingMethodId: string, shippingAddress?: Address) => {
        const {
            data: { checkout }
        } = this.props;

        const updateCartDeliverySpecificationInput = {
            deliveryModeId: shippingMethodId,
            shippingAddress: shippingAddress
        };

        const result = await checkout?.result?.checkoutCart.updateCartDeliverySpecification(updateCartDeliverySpecificationInput);
        if (result?.status !== 'SUCCESS') {
            throw new Error(result?.errorDetails?.LocalizedMessage);
        }
    };

    /**
     * Shows error message on screen next to Apple Pay button.
     * @param message {string} The error message to show.
     */
    private showErrorMessage = (message: string) => {
        this.setState({
            errorMessage: message
        });
    };
}

export default CheckoutApplePay;
